/**
 * The File Shares folder's names-required state (S7 §3.2; review D F8 / Codex C finding 4). An Entra identity with only
 * a data role must name its shares: the folder then shows the probe's sentence and "Open share by name…", nothing else.
 * The connect's `account-info` can answer AFTER the folder was expanded, so the answer is read again when a listing
 * resolves (`namesRequiredDetail`), and a rebuild drops loaded children built for another answer
 * (`fileFolderChildrenOutdated`) instead of carrying "No file shares yet" + Create share over it.
 */
import type { DatabaseExplorerNode, StorageServiceFolderNode } from '../../explorer/models/nodeTypes';
import { getStorageAccountInfoSnapshot } from '../store/storageAccountInfoStore';

/** The row id the names-required sentence carries (`<folder id>-names-required`). */
export const namesRequiredRowId = (folderId: string): string => `${folderId}-names-required`;

/** The probe's sentence when this connection's File service needs share names, else undefined. */
export function namesRequiredDetail(connectionId: string): string | undefined {
  const file = getStorageAccountInfoSnapshot(connectionId)?.services.file;
  return file?.state === 'unverified' && file.reason === 'share-names-required' ? file.detail : undefined;
}

/** Loaded File Shares children that were built for another File availability than the one known now. */
export function fileFolderChildrenOutdated(node: DatabaseExplorerNode): boolean {
  if (node.type !== 'storage-service-folder' || node.isLoading) return false;
  const folder = node as StorageServiceFolderNode;
  const children = folder.children ?? [];
  if (folder.metadata.service !== 'file' || children.length === 0) return false;
  const builtForNames = children.some((child) => child.id === namesRequiredRowId(folder.id));
  return builtForNames !== (namesRequiredDetail(folder.metadata.connectionId) !== undefined);
}
